import { useEffect, useState } from 'react';
import { useAuthStore } from '@/store/authStore';
import { apiClient } from '@/services/apiClient';

function resolvePhotoRequestUrl(photoUrl?: string | null): string {
  const fallback = '/user/photo';
  const raw = String(photoUrl ?? '').trim();

  if (raw === '') {
    return fallback;
  }

  try {
    const parsed = new URL(raw, window.location.origin);
    const relative = `${parsed.pathname}${parsed.search}`;
    return relative.startsWith('/api/v5/') ? relative.slice('/api/v5'.length) : relative;
  } catch {
    return fallback;
  }
}

export function useUserPhoto(photoUrl?: string | null): string | undefined {
  const token = useAuthStore((s) => s.token);
  const accountId = useAuthStore((s) => s.accountId);
  const [url, setUrl] = useState<string>();

  useEffect(() => {
    if (!token || !photoUrl) {
      setUrl(undefined);
      return;
    }

    let cancelled = false;
    let objectUrl: string | undefined;

    apiClient
      .get<{ contentType?: string; data?: string }>(resolvePhotoRequestUrl(photoUrl), {
        headers: {
          Accept: 'image/*',
        },
      })
      .then((response) => {
        if (cancelled) {
          return;
        }

        const raw = response.data;
        if (raw && typeof raw.data === 'string' && typeof raw.contentType === 'string') {
          try {
            const bytes = Uint8Array.from(atob(raw.data), (c) => c.charCodeAt(0));
            const blob = new Blob([bytes], { type: raw.contentType });
            if (blob.size === 0) {
              setUrl(undefined);
              return;
            }
            objectUrl = URL.createObjectURL(blob);
            setUrl(objectUrl);
          } catch {
            setUrl(undefined);
          }
          return;
        }

        setUrl(undefined);
      })
      .catch(() => {
        if (!cancelled) {
          setUrl(undefined);
        }
      });

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [photoUrl, token, accountId]);

  return url;
}
